import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type IUserInfo = {
  avatar: string;
  display_name: string;
  email: string;
  hash_id: string;
  name: string;
  user_id: string;
};

type ISummarySentence = {
  active_speaker_thumbnail_url: string;
  header: string;
  hide_speaker: boolean;
  sentence_hash_id: string;
  shared_screen_thumbnail_url: string;
  text: string;
  timestamp: number;
  user_info: IUserInfo;
};

type IVideoTab = {
  videoHashId: string; //keyId
  videoShotId?: string;
  overview?: any;
  summaries?: any;
  thumbnails?: any;
  transcripts?: any;
  timelines?: any;
  search?: any;
  chapters?: any;
  // people?: any;
};

export type secondaryViewTabState = {
  videos: IVideoTab[];
};

const initialState: secondaryViewTabState = {
  videos: []
};

const SecondaryVideoTabsDataSlice = createSlice({
  name: 'secodary-viewtab',
  initialState: initialState,
  reducers: {
    addOverView(state, action: PayloadAction<{ videoHashId: string; overview: any }>) {
      const { videoHashId, overview } = action.payload;
      // Check if the videoHashId already exists in the state
      const existingIndex = state.videos.findIndex((video) => video.videoHashId === videoHashId);
      if (existingIndex !== -1) {
        // If the videoHashId already exists, update its overview
        state.videos[existingIndex].overview = overview;
      } else {
        // If the videoHashId doesn't exist, add a new entry
        state.videos.push({ videoHashId, overview });
      }
    },
    addData(
      state,
      action: PayloadAction<{
        videoHashId: string;
        videoShotId: string;
        key: 'overview' | 'summaries' | 'thumbnails' | 'transcripts' | 'timelines' | 'search' | 'chapters';
        data: any;
      }>
    ) {
      const { videoHashId, key, data, videoShotId } = action.payload;
      // Check if the videoHashId already exists in the state
      const existingIndex = state.videos.findIndex((video) => video.videoHashId === videoHashId);

      if (existingIndex !== -1) {
        // If the videoHashId already exists, update its data based on the key
        state.videos[existingIndex][key] = data;
      } else {
        // If the videoHashId doesn't exist, add a new entry
        const newVideo: IVideoTab = { videoHashId, videoShotId };
        newVideo[key] = data;
        state.videos.push(newVideo);
      }
    },
    addSummary(state, action: PayloadAction<{ videoShotId: string; summary: ISummarySentence }>) {
      const { videoShotId, summary } = action.payload;
      const existingIndex = state.videos.findIndex((video) => video.videoShotId === videoShotId);
      if (existingIndex !== -1) {
        if (state.videos[existingIndex].summaries) {
          // Add the new summary
          state.videos[existingIndex].summaries?.results.push(summary);
          // Sort summaries by timestamp
          state.videos[existingIndex].summaries?.results.sort((a: any, b: any) => a.timestamp - b.timestamp);
        }
      }
    },
    updateSummary(state, action: PayloadAction<{ videoShotId: any; sentenceHashId: any; sentence: any }>) {
      const { videoShotId, sentenceHashId, sentence } = action.payload;
      const existingIndex = state.videos.findIndex((video) => video.videoShotId === videoShotId);
      if (existingIndex !== -1 && state.videos[existingIndex].summaries) {
        const results = state.videos[existingIndex].summaries?.results;
        // Find the index of the summary sentence that matches the sentenceHashId
        const summaryIndex = results.findIndex((sentence: any) => sentence.sentence_hash_id === sentenceHashId);
        if (summaryIndex !== -1) {
          // Update the text
          state.videos[existingIndex].summaries.results[summaryIndex] = sentence;
        }
      }
    },
    removeSummary(state, action: PayloadAction<{ videoShotId: string; sentenceHashId: string }>) {
      const { videoShotId, sentenceHashId } = action.payload;
      const existingVideoIndex = state.videos.findIndex((video) => video.videoShotId === videoShotId);

      if (existingVideoIndex !== -1 && state.videos[existingVideoIndex].summaries) {
        const summaries = state.videos[existingVideoIndex].summaries;
        if (summaries && summaries?.results) {
          const summaryIndex = summaries.results.findIndex(
            (summary: ISummarySentence) => summary.sentence_hash_id === sentenceHashId
          );
          if (summaryIndex !== -1) {
            // Remove the summary at the found index
            summaries.results.splice(summaryIndex, 1);
          }
        }
      }
    },
    updateTimelineSummary(state, action: PayloadAction<{ videoShotId: any; timelinehashId: any; timeline: any }>) {
      const { videoShotId, timelinehashId, timeline } = action.payload;
      const existingVideo = state.videos.find((video) => video.videoShotId === videoShotId);

      if (existingVideo) {
        // Helper function to update a specific timeline by `hash_id`
        const updateTimeline = (timelineCollection: any[]) => {
          const timelineIndex = timelineCollection.findIndex((t) => t.hash_id === timelinehashId);
          if (timelineIndex !== -1) {
            timelineCollection[timelineIndex] = { ...timelineCollection[timelineIndex], ...timeline };
          }
        };
        // Update `timelines.results` if it exists
        if (existingVideo.timelines?.results) {
          updateTimeline(existingVideo.timelines.results);
        }
        // Update `search.timeline` if it exists
        if (existingVideo.search) {
          existingVideo.search.forEach((searchItem: any) => {
            if (searchItem.timeline) {
              updateTimeline(searchItem.timeline);
            }
          });
        }
      }
    },
    removeTimeline(state, action: PayloadAction<{ videoShotId: string; timelinehashId: string }>) {
      const { videoShotId, timelinehashId } = action.payload;
      const existingVideo = state.videos.find((video) => video.videoShotId === videoShotId);

      if (existingVideo) {
        const timelines = existingVideo.timelines;
        if (timelines && timelines?.results) {
          const timelineIndex = timelines.results.findIndex((timeline: any) => timeline.hash_id === timelinehashId);
          if (timelineIndex !== -1) {
            // Remove the summary at the found index
            timelines.results.splice(timelineIndex, 1);
          }
        }

        // Remove `search.timeline` if it exists
        if (existingVideo.search) {
          existingVideo.search.forEach((searchItem: any) => {
            if (searchItem.timeline) {
              const searchTimelineIndex = searchItem.timeline.findIndex(
                (timeline: any) => timeline.hash_id === timelinehashId
              );
              searchItem.timeline.splice(searchTimelineIndex, 1);
            }
          });
        }
      }
    },
    addTimelineSummary(state, action: PayloadAction<{ videoShotId: string; timeline: any }>) {
      const { videoShotId, timeline } = action.payload;
      const existingIndex = state.videos.findIndex((video) => video.videoShotId === videoShotId);
      if (existingIndex !== -1) {
        if (state.videos[existingIndex].timelines) {
          // Add the new summary
          state.videos[existingIndex].timelines?.results.push(timeline);
          // Sort summaries by timestamp
          state.videos[existingIndex].timelines?.results.sort((a: any, b: any) => a.timestamp - b.timestamp);
        }
      }
    },
    resetTimelineUpdateStatus(state, action: PayloadAction<{ videoShotId: string; status: boolean }>) {
      const { videoShotId, status } = action.payload;
      const existingIndex = state.videos.findIndex((video) => video.videoShotId === videoShotId);
      if (existingIndex !== -1) {
        if (state.videos[existingIndex].timelines) {
          state.videos[existingIndex].timelines.hasUpdated = status;
        }
      }
    },
    updateTranscriptContent(
      state,
      action: PayloadAction<{ videoShotId: any; transcriptHashId: any; transcript: any }>
    ) {
      const { videoShotId, transcriptHashId, transcript } = action.payload;
      const existingVideo = state.videos.find((video) => video.videoShotId === videoShotId);
      if (existingVideo) {
        // Helper function to update a specific timeline by `hash_id`
        const updateTranscript = (trascriptCollection: any[]) => {
          const transcriptIndex = trascriptCollection.findIndex((t) => t.hash_id === transcriptHashId);
          if (transcriptIndex !== -1) {
            trascriptCollection[transcriptIndex] = { ...trascriptCollection[transcriptIndex], ...transcript };
          }
        };
        // Update `transcripts.results` if it exists
        if (existingVideo.transcripts?.results) {
          updateTranscript(existingVideo.transcripts.results);
        }
      }
    },
    removeTranscriptItem(state, action: PayloadAction<{ videoShotId: string; transcriptHashId: string }>) {
      const { videoShotId, transcriptHashId } = action.payload;
      const existingVideo = state.videos.find((video) => video.videoShotId === videoShotId);
      if (existingVideo) {
        const transcripts = existingVideo.transcripts;
        if (transcripts && transcripts?.results) {
          const transcriptIndex = transcripts.results.findIndex(
            (transcript: any) => transcript.hash_id === transcriptHashId
          );
          if (transcriptIndex !== -1) {
            // Remove the transcript at the found index
            transcripts.results.splice(transcriptIndex, 1);
          }
        }
      }
    }
  }
});

export const {
  addOverView,
  addData,
  addSummary,
  removeSummary,
  updateSummary,
  updateTimelineSummary,
  removeTimeline,
  addTimelineSummary,
  resetTimelineUpdateStatus,
  updateTranscriptContent,
  removeTranscriptItem
} = SecondaryVideoTabsDataSlice.actions;

export const secondaryViewTabReducers = SecondaryVideoTabsDataSlice.reducer;
